import React from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Calendar } from '../ui/calendar';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '../ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import { Calendar as CalendarIcon, X, RotateCcw } from 'lucide-react';
import { format } from 'date-fns';
import { VISIBILITY_OPTIONS } from './fileConfig';

export const FileFiltersDrawer = ({
  open,
  onClose,
  filters,
  onFiltersChange,
  onApply,
  onClear,
  categories = [],
  users = [],
  tags = []
}) => {
  const handleFilterChange = (key, value) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const handleCategoryToggle = (categoryId) => {
    const current = filters.categories || [];
    const updated = current.includes(categoryId)
      ? current.filter(c => c !== categoryId)
      : [...current, categoryId];
    handleFilterChange('categories', updated);
  };

  const handleTagToggle = (tag) => {
    const current = filters.tags || [];
    const updated = current.includes(tag)
      ? current.filter(t => t !== tag)
      : [...current, tag];
    handleFilterChange('tags', updated);
  };

  const activeCount = [
    (filters.categories || []).length,
    (filters.tags || []).length,
    filters.visibility ? 1 : 0,
    filters.uploaded_by ? 1 : 0,
    filters.date_from ? 1 : 0,
    filters.date_to ? 1 : 0,
    filters.file_type ? 1 : 0
  ].reduce((a, b) => a + b, 0);

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-[400px] sm:w-[450px]">
        <SheetHeader>
          <SheetTitle className="flex items-center justify-between">
            <span>Filter Files</span>
            {activeCount > 0 && (
              <Badge variant="secondary">{activeCount} active</Badge>
            )}
          </SheetTitle>
          <SheetDescription>
            Narrow down files by category, type, uploader, and more.
          </SheetDescription>
        </SheetHeader>

        <div className="py-6 space-y-6 overflow-y-auto max-h-[calc(100vh-200px)]">
          {/* Category Filter */}
          <div>
            <Label className="text-sm font-medium mb-3 block">Categories</Label>
            <div className="flex flex-wrap gap-2">
              {categories.map(cat => {
                const isSelected = (filters.categories || []).includes(cat.id);
                return (
                  <Badge
                    key={cat.id}
                    variant={isSelected ? 'default' : 'outline'}
                    className="cursor-pointer gap-1"
                    style={isSelected ? { backgroundColor: cat.color } : {}}
                    onClick={() => handleCategoryToggle(cat.id)}
                  >
                    {!isSelected && (
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color }} />
                    )}
                    {cat.name}
                    {isSelected && <X className="h-3 w-3 ml-1" />}
                  </Badge>
                );
              })}
            </div>
          </div>

          {/* Visibility Filter */}
          <div>
            <Label className="text-sm font-medium mb-3 block">Visibility</Label>
            <Select
              value={filters.visibility || 'all'}
              onValueChange={(val) => handleFilterChange('visibility', val === 'all' ? null : val)}
            >
              <SelectTrigger>
                <SelectValue placeholder="All visibility" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {VISIBILITY_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* File Type Filter */}
          <div>
            <Label className="text-sm font-medium mb-3 block">File Type</Label>
            <Select
              value={filters.file_type || 'all'}
              onValueChange={(val) => handleFilterChange('file_type', val === 'all' ? null : val)}
            >
              <SelectTrigger>
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="image">Images</SelectItem>
                <SelectItem value="pdf">PDFs</SelectItem>
                <SelectItem value="document">Documents</SelectItem>
                <SelectItem value="spreadsheet">Spreadsheets</SelectItem>
                <SelectItem value="text">Text files</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Uploaded By Filter */}
          <div>
            <Label className="text-sm font-medium mb-3 block">Uploaded By</Label>
            <Select
              value={filters.uploaded_by || 'all'}
              onValueChange={(val) => handleFilterChange('uploaded_by', val === 'all' ? null : val)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Anyone" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Anyone</SelectItem>
                {users.map(user => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.name || user.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Date Range Filter */}
          <div>
            <Label className="text-sm font-medium mb-3 block">Upload Date Range</Label>
            <div className="flex gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="flex-1 justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {filters.date_from ? format(new Date(filters.date_from), 'MMM d, yyyy') : 'From'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={filters.date_from ? new Date(filters.date_from) : undefined}
                    onSelect={(date) => handleFilterChange('date_from', date?.toISOString() || null)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>

              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="flex-1 justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {filters.date_to ? format(new Date(filters.date_to), 'MMM d, yyyy') : 'To'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={filters.date_to ? new Date(filters.date_to) : undefined}
                    onSelect={(date) => handleFilterChange('date_to', date?.toISOString() || null)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Tags Filter */}
          {tags.length > 0 && (
            <div>
              <Label className="text-sm font-medium mb-3 block">Tags</Label>
              <div className="flex flex-wrap gap-2">
                {tags.map(({ tag, count }) => {
                  const isSelected = (filters.tags || []).includes(tag);
                  return (
                    <Badge
                      key={tag}
                      variant={isSelected ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => handleTagToggle(tag)}
                    >
                      {tag}
                      <span className="ml-1 text-xs opacity-70">({count})</span>
                      {isSelected && <X className="h-3 w-3 ml-1" />}
                    </Badge>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <SheetFooter className="flex gap-2">
          <Button variant="outline" onClick={onClear} className="gap-1">
            <RotateCcw className="h-4 w-4" />
            Clear All
          </Button>
          <Button onClick={onApply}>Apply Filters</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
